import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Font,
  StyleSheet,
} from "@react-pdf/renderer";
import { SMARTBALLOT_LOGO_MONO } from "../assets/smartBallotLogoMono";
import { PDF_FONT_REGULAR, PDF_FONT_BOLD } from "../assets/pdfFont";
import { formatCurrency, ELECTION_TYPES, GOVERNING_REGULATIONS, BASE_INCLUSIONS, NOMINATION_INCLUSION } from "../lib/costEngine";

Font.register({
  family: "SmartBallot",
  fonts: [
    { src: PDF_FONT_REGULAR },
    { src: PDF_FONT_BOLD, fontWeight: "bold" },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

const BLACK = "#111111";
const GRAY = "#333333";
const HAIR = "#000000";
const LINE = "#BBBBBB";

const styles = StyleSheet.create({
  page: { paddingTop: 54, paddingBottom: 84, paddingHorizontal: 54, fontSize: 10, color: BLACK, fontFamily: "SmartBallot", lineHeight: 1.5 },
  // Cover
  logo: { width: 150, height: 60, marginBottom: 18 },
  eyebrow: { fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: GRAY },
  docTitle: { fontSize: 22, fontFamily: "SmartBallot", fontWeight: "bold", marginTop: 8, lineHeight: 1.2 },
  recipientBlock: { marginTop: 20 },
  recipientLine: { fontSize: 11, marginBottom: 2 },
  recipientName: { fontSize: 13, fontFamily: "SmartBallot", fontWeight: "bold", marginBottom: 2 },
  coverEventTitle: { fontSize: 10, color: GRAY, marginTop: 4 },
  metaLine: { fontSize: 9.5, color: GRAY, marginTop: 12 },
  coverTotalLabel: { fontSize: 9.5, letterSpacing: 1, textTransform: "uppercase", color: GRAY, marginTop: 0 },
  coverTotalVal: { fontSize: 18, fontFamily: "SmartBallot", fontWeight: "bold", color: BLACK, marginTop: 2 },
  coverWebsite: { fontSize: 18, fontFamily: "SmartBallot", fontWeight: "bold", color: BLACK, marginTop: 12 },
  coverRule: { borderBottomWidth: 0.75, borderBottomColor: LINE, marginTop: 28, marginBottom: 16 },
  prepBlock: { marginBottom: 10 },
  confTitleTop: { fontSize: 10, fontFamily: "SmartBallot", fontWeight: "bold", letterSpacing: 1, marginTop: 12, marginBottom: 4 },
  rule: { borderBottomWidth: 1, borderBottomColor: HAIR, marginTop: 18, marginBottom: 4 },
  confTitle: { fontSize: 10, fontFamily: "SmartBallot", fontWeight: "bold", letterSpacing: 1, marginTop: 24, marginBottom: 4 },
  confText: { fontSize: 9, color: GRAY },
  // Sections
  h2: { fontSize: 11.5, fontFamily: "SmartBallot", fontWeight: "bold", letterSpacing: 1, textTransform: "uppercase", marginTop: 20, marginBottom: 8, borderBottomWidth: 1, borderBottomColor: HAIR, paddingBottom: 4 },
  h3: { fontSize: 10.5, fontFamily: "SmartBallot", fontWeight: "bold", marginTop: 10, marginBottom: 3 },
  p: { fontSize: 10, color: GRAY, marginBottom: 7 },
  pTop: { fontSize: 10, color: GRAY, marginBottom: 7, marginTop: 8 },
  // Bullets
  bulletRow: { flexDirection: "row", marginBottom: 4 },
  bulletDot: { width: 12, fontSize: 10, color: BLACK },
  bulletText: { flex: 1, fontSize: 10, color: GRAY },
  bulletLabel: { fontFamily: "SmartBallot", fontWeight: "bold", color: BLACK },
  // Two-column tables
  tHead: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: HAIR, paddingBottom: 4, marginTop: 4 },
  tHeadCell: { fontSize: 9.5, fontFamily: "SmartBallot", fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5 },
  tRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: LINE, paddingVertical: 5 },
  colA: { flex: 1, paddingRight: 8, fontSize: 9.5, color: BLACK, fontFamily: "SmartBallot", fontWeight: "bold" },
  colB: { flex: 1.4, fontSize: 9.5, color: GRAY },
  // Pricing table
  meta: { flexDirection: "row", marginBottom: 3 },
  metaLabel: { width: 150, fontSize: 9.5, fontFamily: "SmartBallot", fontWeight: "bold", color: BLACK },
  metaVal: { flex: 1, fontSize: 9.5, color: GRAY },
  priceHead: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: HAIR, paddingBottom: 4, marginTop: 6 },
  priceRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: LINE, paddingVertical: 5 },
  priceRowLast: { flexDirection: "row", paddingVertical: 5 },
  priceRowTotalsLine: { flexDirection: "row", paddingVertical: 2 },
  cItem: { flex: 3, fontSize: 9.5, color: BLACK },
  cNum: { flex: 1, fontSize: 9.5, textAlign: "right", color: GRAY },
  cHeadItem: { flex: 3, fontSize: 9, fontFamily: "SmartBallot", fontWeight: "bold", textTransform: "uppercase" },
  cHeadNum: { flex: 1, fontSize: 9, fontFamily: "SmartBallot", fontWeight: "bold", textAlign: "right", textTransform: "uppercase" },
  subLabel: { flex: 5, textAlign: "right", fontSize: 9.5, fontFamily: "SmartBallot", fontWeight: "bold" },
  subVal: { flex: 1, textAlign: "right", fontSize: 9.5, fontFamily: "SmartBallot", fontWeight: "bold" },
  totalRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: HAIR, borderBottomWidth: 1, borderBottomColor: HAIR, paddingVertical: 7, marginTop: 2 },
  totalLabel: { flex: 5, textAlign: "right", fontSize: 11, fontFamily: "SmartBallot", fontWeight: "bold" },
  totalVal: { flex: 1, textAlign: "right", fontSize: 11, fontFamily: "SmartBallot", fontWeight: "bold" },
  itemNote: { fontSize: 8, color: GRAY, marginTop: 1 },
  inclTitle: { fontSize: 9, fontFamily: "SmartBallot", fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 10, marginBottom: 4 },
  inclWrap: { flexDirection: "row", flexWrap: "wrap" },
  inclRow: { flexDirection: "row", width: "50%", marginBottom: 3 },
  inclDot: { width: 10, fontSize: 9 },
  inclText: { flex: 1, fontSize: 9, color: GRAY },
  // Closing
  signBlock: { marginTop: 14 },
  signName: { fontSize: 10, fontFamily: "SmartBallot", fontWeight: "bold" },
  signLine: { fontSize: 9.5, color: GRAY, marginTop: 1 },
  signContact: { fontSize: 10.5, fontFamily: "SmartBallot", fontWeight: "bold", marginTop: 10 },
  territory: { fontSize: 10, color: GRAY, marginBottom: 6, lineHeight: 1.5 },
  footerRule: { position: "absolute", bottom: 56, left: 54, right: 54, borderBottomWidth: 0.5, borderBottomColor: LINE },
  footerLeft: { position: "absolute", bottom: 34, left: 54, right: 96, fontSize: 8, color: GRAY, lineHeight: 1.3 },
  footerRight: { position: "absolute", bottom: 34, right: 54, fontSize: 8, color: GRAY },
});

const PRINCIPLES = [
  ["Transparency", "clear procedures, documented controls, and defensible reporting"],
  ["Accessibility", "voter support services and multiple voting options such as mail-in, digital, and in-person"],
  ["Security", "controlled processes, ballot reconciliation procedures, and secure voting infrastructure"],
  ["Independence", "objective administration that supports procedural fairness and confidence in results"],
  ["Accountability", "certified reports, detailed documentation, and audit-ready records"],
];

const SERVICES = [
  ["Governance Administration", "SmartBallot establishes and administers voting procedures, operational controls, and governance processes. Our team ensures compliance with applicable rules and procedures while supporting transparency, fairness, and confidence throughout the voting event."],
  ["Voting Operations", "SmartBallot manages electronic, mail-in, and in-person voting processes, including polling station coordination, ballot control, reconciliation, and chain-of-custody procedures. Our objective is to ensure secure, reliable, and professionally managed voting operations."],
  ["Voter Communications & Support", "SmartBallot provides voter communications, election notices, voting instructions, and responsive support services. Our team assists voters through telephone, email, web-based, and in-person channels to help maximize participation and resolve voting issues efficiently."],
  ["Reporting & Certification", "SmartBallot supervises ballot counting, certifies voting results, and documents reconciliation and validation procedures. We provide defensible reporting and audit-ready records that support confidence in the outcome and integrity of the voting event."],
];

const CHALLENGES = [
  ["Inaccurate voter lists", "Verification and reconciliation procedures"],
  ["Low voter participation", "Early communications, notice posting, and voter support services"],
  ["Governance disputes", "Clearly documented procedures"],
  ["Remote participation", "Digital and hybrid voting options"],
  ["Results challenges", "Certified reporting and auditable records"],
  ["Voter identity and eligibility dispute", "Secured identity verification process with documented eligibility controls"],
];

const PHASES = [
  ["Initiation", "Confirm scope, timelines, governance requirements"],
  ["Configuration", "Establish voting procedures, communications, tools"],
  ["Pre-Vote Operations", "Notices, voter support, ballot preparation"],
  ["Voting Period", "Execute and supervise voting operations"],
  ["Certification & Reporting", "Reconciliation, certification, final reporting"],
  ["Close-Out", "Records completion and project closure"],
];

const Bullet = ({ label, text }) => (
  <View style={styles.bulletRow}>
    <Text style={styles.bulletDot}>•</Text>
    <Text style={styles.bulletText}>
      {label ? <Text style={styles.bulletLabel}>{label}: </Text> : null}
      {text}
    </Text>
  </View>
);

const TwoCol = ({ headA, headB, rows }) => (
  <View>
    <View style={styles.tHead}>
      <Text style={[styles.colA, styles.tHeadCell]}>{headA}</Text>
      <Text style={[styles.colB, styles.tHeadCell]}>{headB}</Text>
    </View>
    {rows.map(([a, b]) => (
      <View style={styles.tRow} key={a}>
        <Text style={styles.colA}>{a}</Text>
        <Text style={styles.colB}>{b}</Text>
      </View>
    ))}
  </View>
);

const ProposalFooter = ({ nation, name, today }) => (
  <>
    <View style={styles.footerRule} fixed />
    <Text style={styles.footerLeft} fixed>
      Respectfully prepared by SmartBallot for {nation} and submitted on {today}.
    </Text>
    <Text style={styles.footerRight} fixed render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
  </>
);

const CoverPage = ({ typeLabel, nation, lead, event, total, footer }) => (
  <Page size="A4" style={styles.page}>
    <Image src={SMARTBALLOT_LOGO_MONO} style={styles.logo} />
    <Text style={styles.eyebrow}>SmartBallot</Text>
    <Text style={styles.docTitle}>{typeLabel} Voting Event{"\n"}Services Proposal</Text>

    <View style={styles.recipientBlock}>
      <Text style={styles.recipientName}>{nation}</Text>
      {lead.name ? <Text style={styles.recipientLine}>{lead.name}{lead.jobTitle ? `, ${lead.jobTitle}` : ""}</Text> : null}
      {event.eventTitle ? <Text style={styles.coverEventTitle}>{event.eventTitle}</Text> : null}
    </View>

    <View style={styles.coverRule} />
    <Text style={styles.coverTotalLabel}>Total Estimated Budget</Text>
    <Text style={styles.coverTotalVal}>{formatCurrency(total)} CAD</Text>
    <Text style={styles.coverWebsite}>smartballot.app</Text>
    {footer}
  </Page>
);

const PreparedForBlock = ({ nation, lead, address, today }) => (
  <>
    <View style={styles.prepBlock}>
      <View style={styles.meta}>
        <Text style={styles.metaLabel}>Prepared For</Text>
        <Text style={styles.metaVal}>{lead.name || nation}{lead.jobTitle ? `, ${lead.jobTitle}` : ""}{lead.email ? ` · ${lead.email}` : ""}</Text>
      </View>
      {address ? (
        <View style={styles.meta}>
          <Text style={styles.metaLabel}>Address</Text>
          <Text style={styles.metaVal}>{address}</Text>
        </View>
      ) : null}
      <View style={styles.meta}>
        <Text style={styles.metaLabel}>Prepared</Text>
        <Text style={styles.metaVal}>{today} · Valid for thirty (30) days from the date of submission</Text>
      </View>
    </View>
    <Text style={styles.confTitleTop}>CONFIDENTIALITY NOTICE</Text>
    <Text style={styles.confText}>
      This proposal contains confidential and proprietary information prepared exclusively for the intended
      recipient. Distribution, reproduction, or disclosure without written permission from SmartBallot is prohibited.
    </Text>
  </>
);

const NarrativeSections = ({ nation, eventName }) => (
  <>
    <Text style={styles.h2}>Executive Summary</Text>
    <Text style={styles.p}>
      Thank you for the opportunity to support {nation} in the administration of {eventName}. SmartBallot
      respectfully submits this proposal to provide governance voting event administration, operational
      management, execution, and reporting services.
    </Text>
    <Text style={styles.p}>
      SmartBallot recognizes that voting events carry significant responsibility. Participants must have confidence
      that the process is fair, transparent, secure, and professionally managed. We specialize in the administration
      of governance voting events for Indigenous governments, unions, associations, member-based organizations, and
      other governance entities across Canada who require trusted, secure, and transparent voting processes.
    </Text>
    <Text style={styles.p}>
      Our role is to provide independent oversight, trusted voting infrastructure, voter support services, and
      certified reporting that allows organizations to focus on governance decisions while we manage the voting
      process itself. Our services combine governance-focused election administration, operational execution,
      digital and hybrid voting infrastructure, voter support services, and certified reporting procedures to ensure
      voting events are conducted with integrity, accessibility, procedural fairness, and operational reliability.
    </Text>

    <Text style={styles.h2}>Why SmartBallot</Text>
    <Text style={styles.p}>
      Voting events are different from commercial surveys and consumer polling. They involve important Indigenous
      governance decisions, leadership elections, treaty matters, ratification votes, membership resolutions, and
      other decisions that require confidence in both outcome and the processes required to achieve it. SmartBallot's
      approach is built around five principles:
    </Text>
    {PRINCIPLES.map(([label, text]) => (
      <Bullet key={label} label={label} text={text} />
    ))}
    <Text style={styles.pTop}>
      SmartBallot is an Indigenous technology company located in Victoria, BC. We have over 35 years of First
      Nation, Métis and Inuit election and voting event management. We are certified to run Indian Act, FNEA and
      Custom elections, ratifications, and referendums. Our team covers all of Canada — we are here to serve your
      Community.
    </Text>
    <Text style={styles.p}>
      Our operational model combines trained election personnel, standardized governance procedures, secure voting
      technology, voter support services, and rigorous reporting practices to deliver trusted and professionally
      managed voting events. From project planning through certification of results, our focus is to ensure voting
      events are conducted fairly, transparently, and in a manner that inspires confidence among leadership,
      participants, and the broader community.
    </Text>

    <Text style={styles.h2}>Understanding of the Event</Text>
    <Text style={styles.p}>
      Based on the information presently available, SmartBallot understands the {eventName} may include some or all of
      the following: voter eligibility administration, voter communications, electronic voting, mail-in ballot
      administration, advance voting opportunities, in-person voting operations, polling station management, ballot
      reconciliation, certified reporting, governance compliance procedures, and scrutineer or observer
      participation. Final scope, timelines, and operational requirements will be confirmed collaboratively during
      project initiation.
    </Text>

    <Text style={styles.h2}>Services &amp; Outcomes</Text>
    {SERVICES.map(([title, text]) => (
      <View key={title} wrap={false}>
        <Text style={styles.h3}>{title}</Text>
        <Text style={styles.p}>{text}</Text>
      </View>
    ))}

    <Text style={styles.h2}>Common Challenges &amp; Solutions</Text>
    <Text style={styles.p}>
      SmartBallot's decades of First Nation, Métis and Inuit experience in providing proven and reliable voting solutions positions us
      to recognize and overcome common voting challenges:
    </Text>
    <TwoCol headA="Challenge" headB="SmartBallot Approach" rows={CHALLENGES} />

    <Text style={styles.h2}>SmartBallot Delivery Model</Text>
    <Text style={styles.p}>
      SmartBallot utilizes a structured governance-event delivery model emphasizing operational consistency,
      transparency, accessibility, accountability, and defensible voting outcomes. Where appropriate, SmartBallot may
      deploy trained field personnel and operational staff to support polling operations, logistics, voter support,
      and event administration activities.
    </Text>

    <Text style={styles.h2}>Project Phases</Text>
    <TwoCol headA="Phase" headB="Description" rows={PHASES} />

    <Text style={styles.h2}>Client Responsibilities</Text>
    <Text style={styles.p}>
      To support successful event delivery, the Client will provide applicable governance rules, policies, bylaws,
      and procedures, as well as voter or member information required for election administration. The Client will
      designate a primary project contact, support timely approvals and operational decisions, and provide access to
      facilities and resources where applicable.
    </Text>

    <Text style={styles.h2}>Assumptions &amp; Project Variables</Text>
    <Text style={styles.p}>
      This proposal assumes timelines are achievable, voter/member information is reasonably accurate, approvals are
      provided within operational timelines, and no substantial changes to scope occur after project initiation. The
      following may materially impact pricing, timelines, or operational requirements: additional polling stations,
      remote or fly-in communities, significant voter list remediation, recounts or appeals, emergency rescheduling,
      governance disputes, or major scope changes.
    </Text>
  </>
);

const PriceHeaderRow = () => (
  <View style={styles.priceHead}>
    <Text style={styles.cHeadItem}>Line Item</Text>
    <Text style={styles.cHeadNum}>Qty</Text>
    <Text style={styles.cHeadNum}>Unit</Text>
    <Text style={styles.cHeadNum}>Amount</Text>
  </View>
);

const LineItemsTable = ({ items, subtotalLabel, subtotalValue }) => (
  <>
    <PriceHeaderRow />
    {items.map((it, i) => (
      <View style={styles.priceRow} key={it.name || `li-${i}`}>
        <View style={styles.cItem}>
          <Text>{it.name}</Text>
          {it.amount == null && it.note ? <Text style={styles.itemNote}>{it.note}</Text> : null}
        </View>
        <Text style={styles.cNum}>{it.qty == null ? "-" : Number(it.qty).toLocaleString()}</Text>
        <Text style={styles.cNum}>{it.unit == null ? "-" : formatCurrency(it.unit)}</Text>
        <Text style={styles.cNum}>{it.amount == null ? "Actual +15%" : formatCurrency(it.amount)}</Text>
      </View>
    ))}
    <View style={styles.priceRowLast}>
      <Text style={styles.subLabel}>{subtotalLabel}</Text>
      <Text style={styles.subVal}>{formatCurrency(subtotalValue)}</Text>
    </View>
  </>
);

const PricingSection = ({ event, estimate, typeLabel, nation, eventName, inclusions, fixedItems, variableItems, taxRate }) => (
  <>
    <Text style={styles.h2}>Pricing Structure</Text>
    <Text style={styles.p}>
      Professional service fees for this Event are based on event complexity, voter volumes, operational scope,
      staffing requirements, voting methodologies, and project timelines. The proposed estimated budget for {nation}{" "}
      is presented below in Fixed and Variable sections and is subject to change based on confirmed event details.
    </Text>

    <View style={styles.meta}><Text style={styles.metaLabel}>Voting Event</Text><Text style={styles.metaVal}>{eventName} ({typeLabel})</Text></View>
    <View style={styles.meta}><Text style={styles.metaLabel}>Governing Regulations</Text><Text style={styles.metaVal}>{GOVERNING_REGULATIONS[event.governingRegulation] || "To be confirmed"}</Text></View>
    <View style={styles.meta}><Text style={styles.metaLabel}>Membership Size</Text><Text style={styles.metaVal}>{Number(event.membershipSize || 0).toLocaleString()}</Text></View>
    <View style={styles.meta}><Text style={styles.metaLabel}>Est. Voter Turnout</Text><Text style={styles.metaVal}>{event.voterTurnout ? `${event.voterTurnout}% (${(estimate.estimatedVoters || 0).toLocaleString()} voters)` : "To be confirmed"}</Text></View>

    <Text style={styles.h3}>Fixed Budget Estimate</Text>
    <LineItemsTable items={fixedItems} subtotalLabel="Fixed Subtotal" subtotalValue={estimate.fixedSubtotal} />

    <Text style={styles.inclTitle}>The base fixed cost includes:</Text>
    <View style={styles.inclWrap}>
      {inclusions.map((inc, i) => (
        <View style={styles.inclRow} key={inc || `inc-${i}`}>
          <Text style={styles.inclDot}>•</Text>
          <Text style={styles.inclText}>{inc}</Text>
        </View>
      ))}
    </View>

    <Text style={styles.h3}>Variable Budget Estimate</Text>
    <LineItemsTable items={variableItems} subtotalLabel="Variable Subtotal" subtotalValue={estimate.variableSubtotal} />

    <View style={styles.priceRowTotalsLine}>
      <Text style={styles.subLabel}>Subtotal (Fixed + Variable)</Text>
      <Text style={styles.subVal}>{formatCurrency(estimate.subtotal)}</Text>
    </View>
    <View style={styles.priceRowTotalsLine}>
      <Text style={styles.subLabel}>GST / Tax ({(taxRate * 100).toFixed(taxRate ? 2 : 0)}%)</Text>
      <Text style={styles.subVal}>{formatCurrency(estimate.tax)}</Text>
    </View>
    <View style={styles.totalRow}>
      <Text style={styles.totalLabel}>Total Estimated Budget (CAD)</Text>
      <Text style={styles.totalVal}>{formatCurrency(estimate.total)}</Text>
    </View>

    <Text style={styles.pTop}>
      Printing, postage, assembly, handling, and related materials are variable costs and subject to actual
      quantities, weight and other factors and may change based on Client needs and voting regulation requirements.
      Travel and travel-related expenses, including airfare, hotels, vehicle rental, mileage, ferries, fuel, and
      related travel expenses, are recovered at cost plus 15%. Supporting receipts or invoices may be provided upon
      request.
    </Text>
  </>
);

const ClosingSections = ({ nation }) => (
  <>
    <Text style={styles.h2}>Exclusions</Text>
    <Text style={styles.p}>
      SmartBallot's services do not include legal advice, litigation support, dispute adjudication, or extraordinary
      governance interventions. Recounts beyond standard certification procedures, as well as activities arising from
      force majeure events, natural disasters, or significant operational disruptions, are outside the scope of this
      proposal. Additional services requested beyond the approved scope may require separate authorization and
      billing.
    </Text>

    <Text style={styles.h2}>Proposal Validity</Text>
    <Text style={styles.p}>
      This proposal is valid for thirty (30) days from the date of submission unless otherwise stated in writing.
    </Text>

    <Text style={styles.h2}>Next Steps</Text>
    <Text style={styles.p}>
      SmartBallot appreciates the opportunity to support {nation} and would be honored to assist in delivering a
      trusted and professionally managed governance voting event. Should this proposal align with the Nation's
      requirements, SmartBallot would welcome the opportunity to discuss any questions, confirm the scope of services,
      and finalize the project plan to support successful event delivery.
    </Text>

    <View style={styles.signBlock} wrap={false}>
      <Text style={styles.territory}>
        Respectfully Submitted By SmartBallot, located on the traditional territories of the lək̓ʷəŋən (Lekwungen)
      </Text>
      <Text style={styles.signLine}>#32-1338 Stellys Cross Roads, Brentwood Bay, BC V8M 0B6</Text>
      <Text style={styles.signContact}>We'd love to hear from you!</Text>
      <Text style={styles.signName}>Tommy Cooke, Client Care</Text>
      <Text style={styles.signLine}>Email Me: tommycooke@smartballot.app</Text>
      <Text style={styles.signLine}>Text Me: 519 319-3149</Text>
    </View>
  </>
);

export default function ProposalDocument({ event, estimate, lead }) {
  const typeLabel = ELECTION_TYPES[event.electionType] ? ELECTION_TYPES[event.electionType].label : "Voting Event";
  const today = new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const nation = lead.firstNation || lead.organization || "your community";
  const eventName = event.eventTitle || typeLabel;
  const address = [lead.street, lead.city, [lead.province, lead.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  const items = estimate.items || [];
  const fixedItems = items.filter((i) => i.section === "fixed");
  const variableItems = items.filter((i) => i.section !== "fixed");
  const isElectionType = event.electionType === "election" || event.electionType === "by_election";
  const inclusions = isElectionType ? [NOMINATION_INCLUSION, ...BASE_INCLUSIONS] : BASE_INCLUSIONS;
  const taxRate = estimate.taxRate || 0;

  const footer = <ProposalFooter nation={nation} name={lead.name} today={today} />;

  return (
    <Document>
      <CoverPage typeLabel={typeLabel} nation={nation} lead={lead} event={event} total={estimate.total} footer={footer} />

      <Page size="A4" style={styles.page}>
        <PreparedForBlock nation={nation} lead={lead} address={address} today={today} />
        <NarrativeSections nation={nation} eventName={eventName} />
        <PricingSection
          event={event}
          estimate={estimate}
          typeLabel={typeLabel}
          nation={nation}
          eventName={eventName}
          inclusions={inclusions}
          fixedItems={fixedItems}
          variableItems={variableItems}
          taxRate={taxRate}
        />
        <ClosingSections nation={nation} />
        {footer}
      </Page>
    </Document>
  );
}
